var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import EventBus, { EventScope } from '@openticket/lib-eventbus';
import { loadStripe } from '@stripe/stripe-js/pure';
import CrossWindowShop from './../shop/messaging';
import Cart from './../cart';
import { Logger, LogLevel, OpenTicketBaseNotifications, ShopError, slugError, } from '../internals';
import { forEach, forOwn, modelSort, setLargeTimeout, } from '../utils';
import { ShopConfig } from './config';
import { GET_BY_REF, GET_BY_VALUE, IGNORE_UNDEFINED, Model, } from './model';
import Price, { IGNORE_OPTIONAL, } from './price';
import Pivot, { PIVOTS_KEY_EVENTS_TICKETS, PIVOTS_KEY_GROUPS_PRODUCTS, PIVOTS_KEY_PRODUCTS_EVENT_DATES, PIVOTS_KEY_PRODUCTS_METADATA, PIVOTS_KEY_PRODUCTS_TICKETS, PIVOTS_KEY_SHOPS_COLLAPSES, PIVOTS_KEY_SHOPS_METADATA, PIVOTS_KEY_SHOPS_PAYMENT_METHODS, PIVOTS_KEY_SHOPS_PRODUCTS, PIVOTS_KEY_SHOPS_TICKETS, PIVOTS_KEY_TICKETS_GROUPS, } from './pivot';
import Coupon, { CouponAction, } from './coupon';
import { ProductGroupType, State, } from './types';
/**
 *  The OpenTicket shop.
 */
export default class OpenTicketShop extends EventScope {
    /**
     *  Constructing a new instance creates an uninitialized *(empty)* instance
     */
    constructor(config) {
        super(new EventBus());
        /**
         * Used by EventScope to determine event bus prefix
         */
        this._eventScope = 'shop';
        /**
         *  Boolean that reflects the initialized state of the class.
         *  @category State
         */
        this.is_initialized = false;
        /**
         *  Boolean that refrects the initializing state of the class
         *  @category State
         */
        this.state = State.Start;
        /**
         *  Boolean that reflects the loading state of the class. It
         *  is loading while setting data, both on initialization as
         *  on overwriting or updating data
         *  @category State
         */
        this.loading = true;
        /**
         *  Contains the errors or missing data after initialization
         */
        this.initErrors = [];
        /**
         *  Containing all the data of the shop, based on the [ShopData](../interfaces/shopdata.html) interface.
         *  @category Data
         */
        this.data = {
            guid: '',
            name: '',
            description: null,
            event_selection: null,
            google_tag: null,
            email_validation_rule: null,
            email_must_be_unique: null,
            company_terms: null,
            css: null,
            facebook_auto_attend: null,
            facebook_page_url: null,
            seats_allow_orphan: null,
            greedy_date_selection: null,
            seats_public_key: null,
            reservation_time: null,
            currency: null,
            global_terms: null,
            seats_allow_orphan_computed: null,
            shop_options: {
                validate: null,
                report: null,
                pdf: null,
                reservations: null,
                payment: null,
                callback: null,
                insert: null,
                return: null,
                receipt: null,
                allow_user_return: null,
                arbitrary: null,
                allow_order_return: null,
                to_zero: null,
                skip_metadata_validation: null,
                send_email: null,
                late_personalization: null,
            },
            // The applied filter for the data
            filters: {},
            // Transformed model maps
            collapses_map: {},
            events_map: {},
            event_dates_map: {},
            groups_map: {},
            metadata_map: {},
            payment_methods_map: {},
            products_map: {},
            tickets_map: {},
            // Sorted model representation collections, holding
            // sorted refs to their maps collection
            collapses: [],
            events: [],
            event_dates: [],
            groups: [],
            payment_methods: [],
            tickets: [],
            // Global data
            booker_metadata: [],
            global_products: [],
            // Dates
            available_future_dates: [],
            available_future_dates_nested: {},
            // Branding
            branding: {
                dashboard_terms: null,
                guid: null,
                locale: null,
                name: null,
                shop_privacy_policy: null,
                shop_terms: null,
                website: null,
            },
            // Company
            company: {
                country: null,
                guid: null,
                locale: null,
                name: null,
                coc_name: null,
                seats_allow_orphan: null,
                seats_public_key: null,
                timezone: null,
                website: null,
            },
        };
        /**
         *  The shop can be put in debug mode.
         *
         *  This will result in less human friendly error messages.
         */
        this._debugMode = false;
        /**
         *  Pivots from the shop data
         */
        this._pivots = new Pivot();
        /**
         *  Holding the original shop data that should be loaded while setting data.
         */
        this._shopDataToLoad = {};
        /**
         *  All model collections, used to build model collection Arrays when loading data.
         */
        this._modelCollections = [
            'collapses',
            'events',
            'event_dates',
            'groups',
            'payment_methods',
            'tickets',
        ];
        /**
         *  Contains the errors or missing data after initialization
         */
        this._initErrors = {
            payment_methods: false,
            items: false,
        };
        this.log = new Logger(this._eventBus, config);
        this.notifications = new OpenTicketBaseNotifications(this);
        this.initialized = new Promise((resolve, reject) => {
            this._resolveInit = resolve;
            this._rejectInit = reject;
        });
    }
    get events() {
        return this._eventBus;
    }
    /**
     *
     *  Async method to initialize an OpenTicketShop instance.
     *  It gets the data of the shop based on the provided shop guid,
     *  transforms it and exposes the data by binding the result to his public properties.
     *
     *  ```ts
     *  const shop: OpenTicketShop = new OpenTicketShop();
     *
     *  const config: ShopConfig = {
     *      baseUrl: 'https://api.myticketshop.nl',
     *      guid: '36a8e380-d09b-11e9-be79-49649df1d206',
     *  };
     *
     *  try {
     *
     *      await shop.init(config);
     *
     *      console.info('Initialized shop:', shop);
     *
     *  } catch (error) {
     *
     *      console.warn('An error occured:', error);
     *
     *  }
     *  ```
     *
     *  A filter can be added to limit the amount of shop data.
     *  E.g. for timeslot shops.
     *
     *  Alternatively the shopdata can be forwarded.
     *
     *  Note: filters and forwarded shopdata can not be used consecutively!
     *
     *  #### Possible errors
     *  Two different types of errors could be thrown while initializing a shop.
     *
     *  | Error                         | Reason                                                      |
     *  | :------------------------ --- | :---------------------------------------------------------- |
     *  | [ShopError](shoperror.html)   | No or invalid [ShopConfig](../interfaces/shopconfig.html)   |
     *  |                               | provided or the API returned invalid data                   |
     *  | [ApiError](apierror.html)     | The used API threw an error                                 |
     *
     *  @param config The [config](../interfaces/shopconfig.html) to use for the shop
     *  @param filter The [filter](../globals.html#shopfilter) to use for getting the shop data at
     *  initialisation (not the filter that got applied for retrieving the provided shop data)
     *  @param shopData The original shop data. Initializes the shop with this data instead
     *  of retrieving it out of the API.
     *
     */
    init(config, filter, shopData) {
        return __awaiter(this, void 0, void 0, function* () {
            // Ensure init can only be called once
            if (this.state === State.Ready) {
                throw this.log._create(LogLevel.Error, ['shop', 'init', 'error', 'initialized'], {
                    state: this.state,
                    errors: this.initErrors,
                });
            }
            else if (this.state !== State.Start) {
                throw this.log._create(LogLevel.Error, ['shop', 'init', 'error', 'loading'], {
                    state: this.state,
                    errors: this.initErrors,
                });
            }
            this.state = State.Initializing;
            // Set the logger entity ID
            this.log.setEntityId(config ? config.guid : '');
            if (config && config.guid) {
                this.log.addStaticValue('shop_id', config.guid);
            }
            try {
                // Ensure the config is complete and valid
                this._config = new ShopConfig(config);
                // Set the log levels
                if (this._config.logLevels) {
                    this.log.setLoggingLevels(this._config.logLevels);
                }
                if (this._config.loggerUrl) {
                    this.log.setLoggingUrl(this._config.loggerUrl);
                }
                else {
                    this.log._create(LogLevel.Info, ['shop', 'init', 'logging', 'no-external']);
                }
                this.log.addStaticValue('shop_id', this._config.guid);
                this.log.addStaticValue('base_url', this._config.baseUrl);
                this._ensureFilterOrShopData(filter, shopData);
                // Get the data of the provided shop guid if the data is not provided
                if (!shopData) {
                    shopData = yield this._getShopData();
                }
                // Transform and expose the data
                yield this._setData(shopData);
                // Check if this window is embedded and setup messaging if so
                if (window.self !== window.top) {
                    this.messaging = new CrossWindowShop(window.parent, this);
                }
                const state = this._determineDataIntegrity();
                if (state === State.Ready) {
                    yield this._finishInit();
                }
                else {
                    this.state = State.Waiting;
                }
            }
            catch (error) {
                this.state = State.Error;
                const initError = this.log._create(LogLevel.Fatal, ['shop', 'init', 'error', 'fatal'], {
                    state: this.state,
                    errors: this.initErrors,
                }, null, error);
                this._rejectInit(initError);
                this.loading = false;
                return this.initialized;
            }
            if (this.state === State.Waiting) {
                const initError = this.log._create(LogLevel.Warning, ['shop', 'init', 'error', 'waiting'], {
                    state: this.state,
                    errors: this.initErrors,
                });
                return Promise.reject(initError);
            }
            this.log._create(LogLevel.Info, ['shop', 'init', 'done'], {
                state: this.state,
                errors: this.initErrors,
            });
            return this.initialized;
        });
    }
    /**
     *  Listen to log events by providing a path and a callback to the `on` function
     *
     *  @param path - Path to listen to
     *  @param cb - Will be called when an error event occurs
     */
    onLog(path, cb) {
        if (typeof path === 'string') {
            path = [path];
        }
        return this.on(['log', ...path], cb, true);
    }
    /**
     * Returns true if the sdk is loaded within an iframe and is connected to an OpenTicket CrossWindowClient
     */
    get isFramed() {
        return window.top !== window.self && this.messaging.isConnected;
    }
    /**
     * Send a notification to cross window client
     * @param notification
     */
    sendClientNotification(notification) {
        this.emit(['client-notification'], Object.assign({ _isOpenticket: true, action: 'client-notification' }, notification), true);
    }
    /**
     *  Used to overwrite the exposed shop data. Makes sure to cast the data in
     *  the models as it should be exposed, that the cart is being re-created
     *  and to set the original shop data to undefined.
     */
    overwriteData(shopData) {
        return __awaiter(this, void 0, void 0, function* () {
            // Nothing to overwrite without data!
            if (!shopData) {
                throw this.log._create(LogLevel.Error, ['shop', 'overwriteData', 'error', 'data'], {
                    state: this.state,
                    errors: this.initErrors,
                });
            }
            // The shop was never initialized, cannot overwrite.
            if (this.state === State.Start) {
                throw this.log._create(LogLevel.Error, ['shop', 'overwriteData', 'error', 'initialized'], {
                    state: this.state,
                    errors: this.initErrors,
                });
            }
            if (this.state !== State.Waiting) {
                // Prevent race conditions by throwing an error if the shop is loading
                if (this.loading) {
                    throw this.log._create(LogLevel.Error, ['shop', 'overwriteData', 'error', 'loading'], {
                        state: this.state,
                        errors: this.initErrors,
                    });
                }
                // Shop is in an invalid state, cannot guarantee anything!
                if (this.state !== State.Ready) {
                    throw this.log._create(LogLevel.Error, ['shop', 'overwriteData', 'error', 'state'], {
                        state: this.state,
                        errors: this.initErrors,
                    });
                }
            }
            try {
                this.loading = true;
                this.state = this.state === State.Waiting ? State.Initializing : State.Loading;
                this.data = OpenTicketShop._castData(shopData);
                // Release all the current items in the cart and create
                // a new cart, as we can't guarantee the validity of the
                // items in the cart after an overwrite
                yield this.cart.removeAllItems();
                this.cart = new Cart(this, this._config, this.log, this._eventBus);
                // Rebuild the collection representation Arrays
                forEach(this._modelCollections, (name) => {
                    this._setRepresentationCollection(name);
                });
                const state = this._determineDataIntegrity();
                if (this.state === State.Initializing && state === State.Ready) {
                    yield this._finishInit();
                }
                else {
                    // Set the state depending on data completeness.
                    this.state = state;
                }
            }
            catch (error) {
                this.state = State.Error;
                const overwriteDataError = this.log._create(LogLevel.Fatal, ['shop', 'overwriteData', 'error', 'fatal'], {
                    state: this.state,
                    errors: this.initErrors,
                }, null, error);
                this.loading = false;
                throw overwriteDataError;
            }
            if (this.state === State.Waiting) {
                this.log._create(LogLevel.Warning, ['shop', 'overwriteData', 'error', 'waiting'], {
                    state: this.state,
                    errors: this.initErrors,
                });
                return;
            }
            this.log._create(LogLevel.Info, ['shop', 'overwriteData', 'done'], {
                state: this.state,
                errors: this.initErrors,
            });
            this.loading = this.state !== State.Ready;
        });
    }
    /**
     *  Used to update the data based on a provided filter.
     */
    updateData(filters) {
        return __awaiter(this, void 0, void 0, function* () {
            // Prevent race conditions by throwing an error if the shop is not initialized yet
            if (!this.is_initialized) {
                throw this.log._create(LogLevel.Error, ['shop', 'updateData', 'error', 'initialized'], {
                    state: this.state,
                    errors: this.initErrors,
                });
            }
            if (!filters) {
                throw this.log._create(LogLevel.Error, ['shop', 'updateData', 'error', 'filters'], {
                    state: this.state,
                    errors: this.initErrors,
                });
            }
            if (this.loading) {
                throw this.log._create(LogLevel.Error, ['shop', 'updateData', 'error', 'loading'], {
                    state: this.state,
                    errors: this.initErrors,
                });
            }
            let data;
            // Recoverable error(s)
            try {
                this.loading = true;
                // Lets the getShopData use the provided filter
                this.data.filters = Object.assign({}, filters);
                data = yield this._getShopData();
            }
            catch (error) {
                // Recoverable error(s)
                const updateDataError = this.log._create(LogLevel.Fatal, ['shop', 'updateData', 'error', 'api'], {
                    state: this.state,
                    errors: this.initErrors,
                }, null, error);
                this.loading = false;
                throw updateDataError;
            }
            // Unrecoverable error(s)
            try {
                yield this._setData(data);
                // Lets the cart update the data of his added
                // items according to the just updated data
                this.cart._shopDataUpdated();
                // Update discounts / hidden states for already present coupons
                forOwn(this.cart.getAppliedCoupons(), (coupon) => {
                    this._updateCouponDiscounts(CouponAction.Update, coupon);
                });
            }
            catch (error) {
                // Unrecoverable error(s)
                this.state = State.Error;
                const overwriteDataError = this.log._create(LogLevel.Fatal, ['shop', 'overwriteData', 'error', 'fatal'], {
                    state: this.state,
                    errors: this.initErrors,
                }, null, error);
                this.loading = false;
                throw overwriteDataError;
            }
            // Emit the shop/update event with the name of
            // the loaded shop and the applied filter
            this.emit(['update'], {
                name: this.data.name,
                filters: filters || null,
            });
            this.log._create(LogLevel.Info, ['shop', 'updateData', 'done'], {
                state: this.state,
                errors: this.initErrors,
            });
            this.loading = false;
            return true;
        });
    }
    /**
     *  Used to invoke a refresh of the data: getting the newest data out of the API.
     */
    refreshData() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.updateData(this.data.filters);
        });
    }
    /**
     *  Used to update the coupon discounts when the coupons change in
     *  the cart. Invoked by the cart event listener that got initialised
     *  in the init method.
     *
     *  @internal: Is and should only be used by the cart to update
     *  the discounts of applied coupons AND on update of the data of the shop.
     */
    _updateCouponDiscounts(action, coupon) {
        coupon.appliesTo.shop = this._calculateCouponApplicableItems(coupon);
        // Collect all events which should have their items check hidden values.
        // This is an Object to enforce uniqueness
        const eventsToCheckForHidden = {};
        // Apply the discounts to the applicable items in the shop
        forEach(coupon.appliesTo.shop, (couponApplicable) => {
            // Always apply the discount when it's about a global product
            if (!couponApplicable.ticket) {
                // Do not start applying when the maximum of applying discounts has already been reached
                if (coupon.appliedCount >= coupon.item.applies_to_count) {
                    return;
                }
                const delta = Coupon.applyDiscount(coupon, action, couponApplicable);
                if (delta !== 0) {
                    this.emit(['product', couponApplicable.product.guid, 'update'], {});
                }
                return;
            }
            // If the ticket is not hidden by the coupon,
            // no need to check it and its event!
            if (couponApplicable.ticket.hide_without_coupon) {
                couponApplicable.ticket.hidden = this._determineTicketHidden(couponApplicable.ticket, action === CouponAction.Remove ? undefined : couponApplicable);
                eventsToCheckForHidden[couponApplicable.ticket.event_id] = null;
            }
            // Get the pricing of the ticket to update his pricing's coupon stats
            const ticketPricing = couponApplicable.ticket.pricing;
            // Make sure the stats of the coupon in question are defined in the ticket's pricing
            ticketPricing.coupons[coupon.item.guid] = ticketPricing.coupons[coupon.item.guid] || 0;
            ticketPricing.coupons_count[coupon.item.guid] = ticketPricing.coupons_count[coupon.item.guid] || 0;
            const actions = action === CouponAction.Update
                ? [CouponAction.Remove, CouponAction.Add]
                : [action];
            const beforeBasePrice = couponApplicable.product.pricing.base_price;
            for (const couponAction of actions) {
                // Get the count of how many times the coupon was applied on a product in the ticket
                const appliedInTicketCount = ticketPricing.coupons_count[coupon.item.guid];
                let openSlotsCount = coupon.item.applies_to_count;
                // Remove any applied cart products from the open slots count,
                // IFF they are at least the same price.
                // Any shop product will show the discounted price,
                // IFF it would get the discounted price on the next addition to the cart.
                // If the current shop product is cheaper than the already applied cart products,
                // it will not be discounted.
                for (const cents in coupon.appliedCounts) {
                    try {
                        if (Number.parseInt(cents) >= couponApplicable.product_original_pricing.base_price) {
                            openSlotsCount -= (coupon.appliedCounts[cents] || 0);
                        }
                    }
                    catch (e) {
                        /* No-op */
                    }
                }
                // The 'applies_to_count' becomes relative to the ticket in the shop, so it may not be applied
                // to more products in a ticket than the 'applies_to_count' prescribes
                if ((couponAction === CouponAction.Add && appliedInTicketCount >= openSlotsCount)
                    || (couponAction === CouponAction.Remove && !appliedInTicketCount)) {
                    continue;
                }
                // Apply the discount and save the applied delta to update the ticket's coupon stats below
                const delta = Coupon.applyDiscount(coupon, couponAction, couponApplicable);
                // Update the stats of the coupon within the ticket
                ticketPricing.coupons[coupon.item.guid] += delta;
                // Only count when a delta got applied (zero delta means no delta applied)
                ticketPricing.coupons_count[coupon.item.guid] += delta === 0
                    ? 0
                    : couponAction === CouponAction.Remove ? -1 : 1;
            }
            if (beforeBasePrice !== couponApplicable.product.pricing.base_price) {
                // Emit triggers on next tick, emitting it now is ok, because it does not contain any data.
                this.emit(['ticket', couponApplicable.ticket.guid, 'update'], {});
            }
            // And remove the collection if there's nothing left after removing
            if (action === CouponAction.Remove) {
                if (ticketPricing.coupons[coupon.item.guid] === 0) {
                    delete ticketPricing.coupons[coupon.item.guid];
                }
                if (ticketPricing.coupons_count[coupon.item.guid] === 0) {
                    delete ticketPricing.coupons_count[coupon.item.guid];
                }
            }
        });
        // Check hidden values for the collected events.
        forEach(Object.getOwnPropertyNames(eventsToCheckForHidden), (eventId) => {
            const event = this.data.events_map[eventId];
            event.hidden = this._determineEventHidden(event, true);
        });
        // Used by the `computeItemsPricing` method for getting the products of a ticket
        const getProductsOfTicket = (ticket) => {
            return ticket.products.all;
        };
        // Recompute the pricing of the products and tickets
        // (!) It should only take the required products into account in this situation
        Price.computeItemsPricing(this.data.products_map, this.data.tickets_map, getProductsOfTicket, IGNORE_OPTIONAL);
    }
    /**
     *  Set the shop to debug mode.
     *
     *  Errors will now be more clear and less user friendly!
     *
     *  @param value Whether or not to set the shop to debug mode
     */
    setDebugMode(value) {
        this._debugMode = value;
    }
    /**
     *  Filters and forwarded shopdata can not be used together.
     *  The filter is used for retrieving filtered shop data,
     *  When shop data is forwarded in the init call,
     *  this should already contain the filter used.
     *
     *  @param filters The [filter](../globals.html#shopfilter) to use for getting the shop data at
     *  initialisation (not the filter that got applied for retrieving the provided shop data)
     *  @param shopData The original shop data. Initializes the shop with this data instead
     *  of retrieving it out of the API.
     */
    _ensureFilterOrShopData(filters, shopData) {
        if (filters && shopData) {
            throw this.log._create(LogLevel.Fatal, ['shop', 'data', 'error', 'exclusive', 'filterdata']);
        }
        // Save the filter to the data so it gets used in the
        // getShopData method and so the developer knows which
        // filter got used for loading the data
        if (filters) {
            this.data.filters = Object.assign({}, filters);
        }
        else {
            this.data.filters = Object.assign({}, shopData ? (shopData.filters || {}) : {});
        }
    }
    /**
     *  The init process' final steps.
     *
     *  This method is separated from the init call,
     *  because the init process can be suspended,
     *  if data is incomplete.
     */
    _finishInit() {
        return __awaiter(this, void 0, void 0, function* () {
            // Handle any third party payment methods.
            // E.g.: Loading stripe instances, to enable fraud detection early!
            // TODO: Should a failure here be be a permanent failure for the shop????
            yield this._initPaymentMethods();
            // Initialize a new cart with the shop, config and event bus
            this.cart = new Cart(this, this._config, this.log, this._eventBus);
            // Resolve the initialized states of the shop
            this.is_initialized = true;
            this.state = State.Ready;
            this._resolveInit(true);
            this.loading = false;
            // Emit the shop/init event with the name of the
            // loaded shop and the possibly applied filter
            this.emit(['init'], {
                name: this.data.name,
                filters: this.data.filters || null,
            });
        });
    }
    /**
     *  Used to cast the needed data of the models in the forwarded data.
     */
    static _castData(shopData) {
        Model.castData.ofEvents(shopData.events_map);
        Model.castData.ofEventDates(shopData.event_dates_map);
        Model.castData.ofTickets(shopData.tickets_map);
        return shopData;
    }
    /**
     *  Used for getting the data of the shop via the configured HTTP client.
     */
    _getShopData() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = this._config.getNoCacheUrl(`${this._config.guid}/data`, this.data.filters);
            try {
                const shop = yield this._config.httpClient.get(url);
                if (!shop.data.filters) {
                    shop.data.filters = Object.assign({}, this.data.filters || {});
                }
                return shop.data;
            }
            catch (error) {
                if (error && error.response && error.response.status === 404) {
                    throw this.log._create(LogLevel.Fatal, ['shop', 'data', 'error', 'notfound'], {}, null, error);
                }
                throw this.log._create(LogLevel.Fatal, ['shop', 'data', 'error', 'retrieval', 'failed'], {}, null, error);
            }
        });
    }
    /**
     *
     *  Used for setting original data and transforming that
     *  data to something we want to expose to the client.
     *
     *  (i) Which is exposed by binding it to this instance
     *
     */
    _setData(shopData) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!Object.keys(shopData).length) {
                throw this.log._create(LogLevel.Fatal, ['shop', 'data', 'error', 'missing', 'data']);
            }
            if (!shopData.shops || !shopData.shops.length) {
                throw this.log._create(LogLevel.Fatal, ['shop', 'data', 'error', 'missing', 'shop']);
            }
            // Set the shopdata to load in the original shop data property,
            // so every method that sets the data has access to the data
            // that should currently be loaded
            this._shopDataToLoad = shopData;
            // Set filter data
            this.data.filters = Object.assign({}, shopData.filters);
            // Set a ref to the original shop data pivots so the pivot logic can easily access it
            this._pivots.setPivots(shopData.pivots);
            // TODO: add parameters and return values to the methods below
            // to indicate what data they access
            // Compute all the model maps
            // (!) Order of execution of the maps is important because of the refs
            this._setEventDatesMap();
            this._setMetadataMap();
            this._setProductsMap();
            this._setGroupsMap();
            this._setTicketsMap();
            this._setCollapsesMap();
            this._setEventsMap();
            this._setPaymentMethodsMap();
            // Set all the model representation collections, which contain
            // sorted references to their corresponding model maps
            forEach(this._modelCollections, (name) => {
                this._setRepresentationCollection(name);
            });
            // Currently the loaded data can only contain a single shop, so
            // forward this shop to set his global data
            const shop = shopData.shops[0];
            const branding = shopData.brandings[0];
            const company = shopData.companies[0];
            this._setGlobalData(shop, branding, company);
            // Add possible dates for timeslot
            if (this.data.greedy_date_selection) {
                this.data.available_future_dates = Array.from(shopData.available_future_dates);
                this.data.available_future_dates_nested = Object.assign({}, shopData.available_future_dates_nested);
            }
            // Clear the shop data to load, as we don't need it
            // anymore once it is transformed and exposed
            this._shopDataToLoad = {};
            return true;
        });
    }
    /**
     *  Determine if the shop data is ready for full initialization of the shop.
     */
    _determineDataIntegrity() {
        this._initErrors.payment_methods = !this.data.payment_methods.length;
        this._initErrors.items = !this.data.tickets.length && !this.data.global_products.length;
        this.initErrors = [];
        if (this._initErrors.payment_methods) {
            this.initErrors.push({
                type: 'payment_methods',
                message: 'Missing payment methods',
            });
        }
        if (this._initErrors.items) {
            this.initErrors.push({
                type: 'items',
                message: 'Missing tickets or products',
            });
        }
        if (this.initErrors.length) {
            return State.Waiting;
        }
        return State.Ready;
    }
    /**
     *  Used to initialize any payment method 3rd party scripts.
     */
    _initPaymentMethods() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.data || !this.data.payment_methods) {
                throw this.log._create(LogLevel.Error, ['shop', 'data', 'error', 'loading', 'paymentmethods']);
            }
            const promises = [];
            forEach(this.data.payment_methods, (paymentMethod) => {
                if (paymentMethod.stripe) {
                    const promise = loadStripe(paymentMethod.stripe.stripe_public_key)
                        .then((stripe) => {
                        if (!stripe || !paymentMethod.stripe) {
                            // TODO should this be a permanent failure for the shop????
                            throw this.log._create(LogLevel.Error, ['shop', 'data', 'error', 'loading', 'stripe']);
                        }
                        paymentMethod.stripe.stripe = stripe;
                    });
                    promises.push(promise);
                }
            });
            yield Promise.all(promises);
        });
    }
    /**
     *  Used to set and expose all the event dates of the shop
     *  as guid map, making sure they are being transformed
     *  before doing so.
     */
    _setEventDatesMap() {
        if (!Array.isArray(this._shopDataToLoad.eventDates)) {
            throw this.log._create(LogLevel.Warning, ['shop', 'data', 'error', 'missing', 'eventdates']);
        }
        forEach(this._shopDataToLoad.eventDates, (eventDate) => {
            Model.addTo(this.data.event_dates_map, this._getTransformedEventDate(eventDate.guid));
        });
    }
    /**
     *  Used to set and expose all the metadata of the shop, making
     *  sure they are being transformed before doing so.
     */
    _setMetadataMap() {
        if (!Array.isArray(this._shopDataToLoad.metaData)) {
            throw this.log._create(LogLevel.Warning, ['shop', 'data', 'error', 'missing', 'metadata']);
        }
        forEach(this._shopDataToLoad.metaData, (metadata) => {
            Model.addTo(this.data.metadata_map, this._getTransformedMetadata(metadata.guid));
        });
    }
    /**
     *  Used to set and expose all the events of the shop as
     *  guid map, making sure they are being transformed
     *  before doing so.
     */
    _setEventsMap() {
        if (!Array.isArray(this._shopDataToLoad.events)) {
            throw this.log._create(LogLevel.Warning, ['shop', 'data', 'error', 'missing', 'events']);
        }
        /**
         *  Keep track of the events that are being added to the events map
         *  for the current dataset to load, so we know for which events we
         *  should invoke an update of the attached models based on the pivots,
         *  because the pivots might have been changed in the new dataset.
         */
        const addedEvents = {};
        forEach(this._shopDataToLoad.events, (event) => {
            addedEvents[event.guid] = event.guid;
            // Create the map with the transformed events
            Model.addTo(this.data.events_map, this._getTransformedEvent(event.guid));
        });
        // Invoke an update of the events' pivots models to make sure
        // they are in sync with the current data to load
        forEach(Object.keys(this.data.events_map), (guid) => {
            if (!addedEvents[guid]) {
                this._enrichEvent(this.data.events_map[guid]);
            }
        });
    }
    /**
     *  Used to set and expose all the collapses of the shop as
     *  guid map, making sure they are being transformed
     *  before doing so.
     */
    _setCollapsesMap() {
        if (!Array.isArray(this._shopDataToLoad.collapses)) {
            throw this.log._create(LogLevel.Warning, ['shop', 'data', 'error', 'missing', 'collapses']);
        }
        // It is not mandatory to have collapses
        if (!this._shopDataToLoad.collapses.length) {
            return;
        }
        // Expose all transformed collapses
        forEach(this._shopDataToLoad.collapses, (collapse) => {
            Model.addTo(this.data.collapses_map, this._getTransformedCollapse(collapse.guid));
        });
        // Compute an index based on the pivots so they can easily be sorted
        const pivotSettings = {
            key: PIVOTS_KEY_SHOPS_COLLAPSES,
            item: {
                guid: '',
                guid_key: 'collapse_guid',
            },
        };
        this._pivots.computeIndexes(this.data.collapses_map, pivotSettings);
        // Always add the number of the tickets it contains to each collapse so
        // they're always at the bottom of the sorted collection
        const numberOfTickets = Object.keys(this.data.tickets_map).length;
        forOwn(this.data.collapses_map, (collapse) => {
            collapse.index += numberOfTickets;
            // But overwrite the index with the top ticket's index if
            // present to make sure it will be sorted at the right
            // position in the event items collection
            collapse.index = collapse.tickets[0]
                ? collapse.tickets[0].index
                : collapse.index;
        });
    }
    /**
     *  Used to transform and expose the general data of the shop, including
     *  the general shop data, booker metadata and payment methods.
     */
    _setGlobalData(shop, branding, company) {
        // Directly expose every primitive value
        forOwn(shop, (value, key) => {
            if (value !== Object(value)) {
                // @ts-ignore: we know it's there
                this.data[key] = value;
            }
        });
        // Directly expose the shop options
        this.data.shop_options = shop.shop_options;
        // The remaining data to expose
        this._setBookerInformation(shop.guid);
        this._setGlobalProducts(shop.guid);
        forOwn(branding, (value, key) => {
            if (value !== Object(value)) {
                // @ts-ignore: we know it's there
                this.data.branding[key] = value;
            }
        });
        forOwn(company, (value, key) => {
            if (value !== Object(value)) {
                // @ts-ignore: we know it's there
                this.data.company[key] = value;
            }
        });
    }
    /**
     *  Used to collect and expose the booker metadata, which basically is
     *  the metadata that applies for the whole shop.
     */
    _setBookerInformation(shopGuid) {
        const metadataPivots = this._pivots.get({
            key: PIVOTS_KEY_SHOPS_METADATA,
            item: {
                guid: shopGuid,
                guid_key: 'shop_guid',
            },
        });
        forEach(metadataPivots, (pivot) => {
            const metadata = Model.getFrom(this.data, 'metadata_map', pivot.metadata_guid);
            const shopMetadata = {
                index: 0,
                guid: metadata.guid,
                item: metadata,
            };
            // Add reference to transformed metadata
            Model.addTo(this.data.booker_metadata, shopMetadata);
        });
        // Add indexes to the metadata based on the
        // pivots, so they can easily be sorted
        const pivotSettings = {
            key: PIVOTS_KEY_SHOPS_METADATA,
            item: {
                guid: '',
                guid_key: 'metadata_guid',
            },
        };
        this._pivots.computeIndexes(this.data.booker_metadata, pivotSettings);
        // Sort the model collection
        this.data.booker_metadata.sort(modelSort);
    }
    /**
     *
     *  Used to expose the global products, which basically are products
     *  that could be purchased on shop level. Exposing them out
     *  of both the global_products as pivots data.
     *
     *  (!) They are first added to the general products collection in the
     *  _setProductsMap() method, before their references are added to the
     *  event's global products or global global_products collection.
     *
     */
    _setGlobalProducts(shopGuid) {
        /**
         *
         *  Fn for adding the global product as ShopProduct to either the
         *  collection of his event or the global products collection.
         *
         *  @param productGuid The guid of the global product
         *  @param index currently random, as there's no
         *  solid way to compute their correct indexes.
         *
         */
        const setGlobalProduct = (productGuid, index) => {
            const product = Model.getFrom(this.data, 'products_map', productGuid);
            const collection = !product.event_id || !this.data.events_map[product.event_id]
                ? this.data.global_products
                : this.data.events_map[product.event_id].global_products;
            const shopProduct = {
                index,
                guid: product.guid,
                item: product,
                pricing: JSON.parse(JSON.stringify(product.pricing)),
            };
            // The original product model does not hold coupons, because coupons
            // are being applied to the ShopProduct wrapper instead
            shopProduct.pricing.coupons = {};
            Model.addTo(collection, shopProduct);
        };
        // Expose returned global products
        if (this._shopDataToLoad.global_products &&
            this._shopDataToLoad.global_products.constructor === Object) {
            forEach(Object.keys(this._shopDataToLoad.global_products), setGlobalProduct);
        }
        // Also expose returned global products based on the pivots, which can be found in the products map
        const shopProductPivots = this._shopDataToLoad.pivots[PIVOTS_KEY_SHOPS_PRODUCTS];
        if (Array.isArray(shopProductPivots) && shopProductPivots.length) {
            const globalProductPivots = this._pivots.get({
                key: PIVOTS_KEY_SHOPS_PRODUCTS,
                item: {
                    guid: shopGuid,
                    guid_key: 'shop_guid',
                },
            });
            forEach(globalProductPivots, (pivot, index) => {
                setGlobalProduct(pivot.product_guid, index);
            });
        }
        this.data.global_products.sort(modelSort);
        forEach(this.data.events, (event) => {
            event.global_products.sort(modelSort);
        });
    }
    /**
     *  Used to set and expose all the payment methods of the shop,
     *  making sure they are being transformed before doing so.
     */
    _setPaymentMethodsMap() {
        if (!Array.isArray(this._shopDataToLoad.paymentmethods)) {
            throw this.log._create(LogLevel.Warning, ['shop', 'data', 'error', 'missing', 'paymentmethods']);
        }
        forEach(this._shopDataToLoad.paymentmethods, (paymentMethod) => {
            Model.addTo(this.data.payment_methods_map, this._getTransformedPaymentMethod(paymentMethod.guid));
        });
        // Add indexes to the payment methods groups based on
        // the pivots so they can easily be sorted
        const pivotSettings = {
            key: PIVOTS_KEY_SHOPS_PAYMENT_METHODS,
            item: {
                guid: '',
                guid_key: 'payment_method_id',
            },
        };
        this._pivots.computeIndexes(this.data.payment_methods_map, pivotSettings);
    }
    /**
     *  Used to set and expose all the products of the shop, both the regular products
     *  as global products; making sure they are being transformed before doing so.
     */
    _setProductsMap() {
        if (!this._shopDataToLoad.products) {
            throw this.log._create(LogLevel.Warning, ['shop', 'data', 'error', 'missing', 'products']);
        }
        /**
         *  Keep track of the products that are being added to the products map
         *  for the current dataset to load, so we know for which products we
         *  should invoke an update of the attached models based on the pivots,
         *  because the pivots might have been changed in the new dataset.
         */
        const addedProducts = {};
        forEach(Object.keys(this._shopDataToLoad.products), (guid) => {
            addedProducts[guid] = guid;
            Model.addTo(this.data.products_map, this._getTransformedProduct(guid));
        });
        // Also add returned global products to the exposed products collection
        if (this._shopDataToLoad.global_products && this._shopDataToLoad.global_products.constructor === Object) {
            forEach(Object.keys(this._shopDataToLoad.global_products), (guid) => {
                addedProducts[guid] = guid;
                Model.addTo(this.data.products_map, this._getTransformedProduct(guid));
            });
        }
        // Invoke an update of the products' pivots models to make sure
        // they are in sync with the current data to load
        forEach(Object.keys(this.data.products_map), (guid) => {
            if (!addedProducts[guid]) {
                this._enrichProduct(this.data.products_map[guid]);
            }
        });
    }
    /**
     *  Used to set and expose all the groups of the shop as
     *  guid map, making sure they are being transformed
     *  before doing so.
     */
    _setGroupsMap() {
        if (!Array.isArray(this._shopDataToLoad.groups)) {
            throw this.log._create(LogLevel.Warning, ['shop', 'data', 'error', 'missing', 'groups']);
        }
        /**
         *  Keep track of the groups that are being added to the groups map
         *  for the current dataset to load, so we know for which groups we
         *  should invoke an update of the attached models based on the pivots,
         *  because the pivots might have been changed in the new dataset.
         */
        const addedGroups = {};
        // Expose all transformed groups
        forEach(this._shopDataToLoad.groups, (group) => {
            addedGroups[group.guid] = group.guid;
            Model.addTo(this.data.groups_map, this._getTransformedProductGroup(group.guid));
        });
        // Invoke an update of the group's pivots models to make sure
        // they are in sync with the current data to load
        forOwn(this.data.groups_map, (group, guid) => {
            if (!addedGroups[guid]) {
                this._enrichGroup(group);
            }
        });
        // Add indexes to the product groups based on
        // the pivots so they can easily be sorted
        const pivotSettings = {
            key: PIVOTS_KEY_TICKETS_GROUPS,
            item: {
                guid: '',
                guid_key: 'group_guid',
            },
        };
        this._pivots.computeIndexes(this.data.groups_map, pivotSettings);
    }
    /**
     *  Used to set and expose all the tickets of the shop as
     *  guid map, making sure they are being transformed
     *  before doing so.
     */
    _setTicketsMap() {
        if (!this._shopDataToLoad.tickets) {
            throw this.log._create(LogLevel.Warning, ['shop', 'data', 'error', 'missing', 'tickets']);
        }
        /**
         *  Keep track of the tickets that are being added to the tickets map
         *  for the current dataset to load, so we know for which tickets we
         *  should invoke an update of the attached models based on the pivots,
         *  because the pivots might have been changed in the new dataset.
         */
        const addedTickets = {};
        // Expose all transformed tickets
        forOwn(this._shopDataToLoad.tickets, (ticket) => {
            addedTickets[ticket.guid] = ticket.guid;
            Model.addTo(this.data.tickets_map, this._getTransformedTicket(ticket.guid));
        });
        // Invoke an update of the ticket' pivots models to make sure
        // they are in sync with the current data to load
        forOwn(this.data.tickets_map, (ticket, guid) => {
            if (!addedTickets[guid]) {
                this._enrichTicket(ticket);
            }
        });
        // Add indexes to the ticket based on the
        // pivots so they can easily be sorted
        const pivotSettings = {
            key: PIVOTS_KEY_SHOPS_TICKETS,
            item: {
                guid: '',
                guid_key: 'ticket_guid',
            },
        };
        this._pivots.computeIndexes(this.data.tickets_map, pivotSettings);
    }
    /**
     *
     *  Used to expose all the models as a representation Array,
     *  containing sorted refs to the original model objects in
     *  the model's guid map.
     *
     *  @param name The name of the collection to set the representation collection of
     *
     */
    _setRepresentationCollection(name) {
        // Get refs to the representation collection and his models map
        const collection = this.data[name];
        const map = this.data[`${name}_map`];
        // Throw an error when the collection or map could not be found
        if (!collection || !map) {
            throw slugError(`The ${!collection ? 'collection' : 'map'} of ${name} is undefined`, ['shop', 'data', 'error', 'representation'], { name });
        }
        // Always clean the collection before filling
        collection.splice(0, collection.length);
        /**
         *  Add refs to every model in the map if no filters were applied or if the shop data to
         *  load is unknown (meaning the shop data got set via the overwrite data method), or
         *  only add models that got loaded according to the current set original shop data.
         *
         *  (i) Adding all models should happen when rebuilding the collections
         *  based on set/overwritten maps in the overwriteData() method.
         */
        if (!Object.keys(this._shopDataToLoad).length ||
            !Object.keys(this.data.filters).length) {
            forOwn(map, (model) => {
                Model.addTo(collection, model);
            });
        }
        else {
            // Some data that is exposed under a specific property
            // might be returned in another property name
            const dataKeyMap = {
                event_dates: 'eventDates',
                payment_methods: 'paymentmethods',
            };
            const dataKey = dataKeyMap[name] || name;
            // Get the loaded data, so we can loop over the models that are currently
            // loaded according to the current set original shop data and we can only
            // add (refs to) them in the representation collection
            // (!) As the map always contains every loaded model
            const loadedData = this._shopDataToLoad[dataKey];
            // Fn to add a reference to the transformed model in the map,
            // based on a model that should currently be added to the
            // collection according to the loaded original data
            const addModelToCollection = (model) => {
                Model.addTo(collection, map[model.guid]);
            };
            if (Array.isArray(loadedData)) {
                forEach(loadedData, addModelToCollection);
            }
            else {
                forOwn(loadedData, addModelToCollection);
            }
        }
        // Sort the model collection
        collection.sort(modelSort);
    }
    /**
     *  Used to get a transformed copy of an event date
     *  out of the original data; casting the dates.
     */
    _getTransformedEventDate(guid) {
        const eventDate = Model.getFrom(this._shopDataToLoad, 'eventDates', guid, GET_BY_VALUE);
        Model.castData.ofEventDate(eventDate);
        return eventDate;
    }
    /**
     *  Used to get a transformed copy of a metadata
     *  out of the original data; casting the dates.
     */
    _getTransformedMetadata(guid) {
        const metadata = Model.getFrom(this._shopDataToLoad, 'metaData', guid, GET_BY_VALUE);
        // @ts-ignore: Always expose the rules as Array
        // noinspection SuspiciousTypeOfGuard
        metadata.extra = typeof metadata.extra === 'string' ? metadata.extra.split('|') : metadata.extra || [];
        // Used for enum values
        metadata.options = [];
        // Filter out empty rules and split the enum values if there are rules
        if (!metadata.extra) {
            return metadata;
        }
        metadata.extra = metadata.extra.filter((rule) => {
            if (!rule.length) {
                return false;
            }
            if (/^in:/.test(rule)) {
                const values = rule.split(':')[1].split(',');
                forEach(values, (value) => {
                    if (!value.length) {
                        return;
                    }
                    metadata.options.push(value);
                });
            }
            return true;
        });
        return metadata;
    }
    /**
     *  Used to get a transformed copy of an event out of the original
     *  data; adding a holder for his global products, his tickets, event
     *  dates, computing his start and end based on the corresponding
     *  event dates and adding his sorted items list.
     */
    _getTransformedEvent(guid) {
        const event = Model.getFrom(this._shopDataToLoad, 'events', guid, GET_BY_VALUE);
        Model.castData.ofEvent(event);
        this._enrichEvent(event);
        return event;
    }
    /**
     *  Used to get a transformed copy of an event out of the original data;
     *  adding the corresponding tickets based on his ticket_guid prop
     *  and casting the dates.
     */
    _getTransformedCollapse(guid) {
        const collapse = Model.getFrom(this._shopDataToLoad, 'collapses', guid, GET_BY_VALUE);
        collapse.tickets = [];
        forEach(collapse.ticket_guids, (ticketGuid) => {
            // Try to get the ticket out of the tickets map
            const ticket = Model.getFrom(this.data, 'tickets_map', ticketGuid, GET_BY_REF, IGNORE_UNDEFINED);
            // Only try to add his reference if it's present
            // (i) It might not be present because it got deleted in
            // the shop but not directly in the collapse
            if (ticket) {
                Model.addTo(collapse.tickets, ticket);
            }
        });
        // Sort the tickets based on their pivot-based computed index
        collapse.tickets.sort(modelSort);
        return collapse;
    }
    /**
     *  Used to get a transformed copy of a payment method out of the original
     *  data; adding a pricing object and moving the fees to that object.
     */
    _getTransformedPaymentMethod(guid) {
        const paymentMethod = Model.getFrom(this._shopDataToLoad, 'paymentmethods', guid, GET_BY_VALUE);
        // @ts-ignore: moving the fees to the pricing object
        const fees = this._transformFees(paymentMethod.fees);
        // @ts-ignore: moving the stripe public key to the stripe object
        const stripePublicKey = paymentMethod.stripe_public_key;
        // @ts-ignore
        delete paymentMethod.fees;
        // @ts-ignore
        delete paymentMethod.stripe_public_key;
        // Enrich the payment method with a pricing object, including the fees
        const pricing = {
            fees,
            vat: {
                percentage: 0,
                amount: 0,
            },
            total_fees: 0,
        };
        // Enrich the payment method with a stripe object
        const stripe = stripePublicKey ? {
            stripe_public_key: stripePublicKey,
            complete: false,
            errors: [],
        } : undefined;
        return Object.assign(Object.assign({}, paymentMethod), { pricing,
            stripe });
    }
    /**
     *  Used to get a transformed copy of a product group out of the original data;
     *  adding the corresponding products based on his members prop.
     */
    _getTransformedProductGroup(guid) {
        const productGroup = Model.getFrom(this._shopDataToLoad, 'groups', guid, GET_BY_VALUE);
        delete productGroup.members;
        // Always clean the products, as the dataset to load always
        // contains all the pivots a group should contain
        productGroup.products = [];
        this._enrichGroup(productGroup);
        // If all products need to be selected to comply with
        // the constraints, the group as a whole is 'required'
        if (productGroup.min_bound === productGroup.max_bound &&
            productGroup.max_bound === productGroup.products.length &&
            productGroup.uniqueness === 1) {
            productGroup.type = ProductGroupType.Required;
            // If only one of each can be selected, it will get a type
            // that resolves to a custom input in the shop
        }
        else if (productGroup.uniqueness === 1) {
            // If there is only one option available and it's
            // optional, the type is 'checkbox'
            if (productGroup.max_bound === 1 &&
                productGroup.min_bound === 0 &&
                productGroup.products.length === 1) {
                productGroup.type = ProductGroupType.Checkbox;
            }
            else if (productGroup.max_bound === 1) {
                // If only on item can be selected of all products in
                // the group, the type will be 'select' or 'radio', as
                // these are the input methods that fit the constraint
                // If the length is larger than 3, the type - and therefore
                // the input shown in the UI - should change
                productGroup.type = productGroup.products.length > 3
                    ? ProductGroupType.Select
                    : ProductGroupType.Radio;
                // If all items can be selected, but all at most once, the
                // group will get the 'multiselect' or 'checkbox' type
            }
            else {
                // If the length is larger than 3, the type - and therefore
                // the input shown in the UI -  should change
                productGroup.type = productGroup.products.length > 3
                    ? ProductGroupType.Multiselect
                    : ProductGroupType.Checkbox;
            }
            // If the previous statements are not true, all products
            // can be selected as long as they are in stock.
            // (i) Optional is the default type for optional products
        }
        else {
            productGroup.type = ProductGroupType.Optional;
        }
        return productGroup;
    }
    /**
     *  Used to get a transformed copy of a product out of the original data;
     *  adding the corresponding metadata and event dates and adding
     *  original and regular pricing object.
     */
    _getTransformedProduct(guid) {
        // Only applies for regular products
        if (!this._shopDataToLoad.products) {
            throw new ShopError('The loaded shop data does not contain products');
        }
        // First try to get the product out of the default products, otherwise
        // out of the global products - but always throw the original error to
        // keep consistent errors
        let product = null;
        let error = null;
        try {
            product = Model.getFrom(this._shopDataToLoad, 'products', guid, GET_BY_VALUE);
        }
        catch (originalError) {
            error = originalError;
        }
        if (!product) {
            try {
                product = Model.getFrom(this._shopDataToLoad, 'global_products', guid, GET_BY_VALUE);
            }
            catch (e) {
                throw error;
            }
        }
        // @ts-ignore Enrich the product with an original pricing object
        const pricing = Price.createPricing(product.price, this._transformFees(product.fees), product.vat_percentage);
        // @ts-ignore: The following props get exposed via the pricing object
        delete product.price;
        // @ts-ignore
        delete product.fees;
        product = Object.assign(Object.assign({}, product), { pricing });
        // Enrich the product with his event dates and metadata
        this._enrichProduct(product);
        // Compute the product pricing
        Price.computeProductPricing(product.pricing);
        // @ts-ignore: The following props get exposed via the pricing object
        delete product.price;
        // @ts-ignore
        delete product.fees;
        return product;
    }
    /**
     *  Used to get a transformed copy of a ticket out of the original data;
     *  adding the corresponding groups and products, original and regular
     *  pricing objects and casting the dates.
     */
    _getTransformedTicket(guid) {
        if (!this._shopDataToLoad.tickets) {
            throw new ShopError('The loaded shop data does not contain tickets');
        }
        let ticket = Model.getFrom(this._shopDataToLoad, 'tickets', guid, GET_BY_VALUE);
        // Cast the data of the ticket
        Model.castData.ofTicket(ticket);
        // @ts-ignore: Enrich the ticket with a ticket pricing object
        const pricing = Price.createTicketPricing(0, this._transformFees(ticket.fees), ticket.vat_percentage);
        // @ts-ignore: Exposed via the pricing object
        delete ticket.fees;
        // Enriching the ticket with his groups and products based on the pivots
        this._enrichTicket(ticket);
        // Compute the ticket's pricing
        Price.computeTicketPricing(pricing, ticket.min_price, ticket.products.all, IGNORE_OPTIONAL);
        ticket = Object.assign(Object.assign({}, ticket), { pricing, available_from_margin: ticket.availability_margin && ticket.available_from ? new Date(ticket.available_from.getTime() - ticket.availability_margin * 60000) : null, available_until_margin: ticket.availability_margin && ticket.available_until ? new Date(ticket.available_until.getTime() + ticket.availability_margin * 60000) : null });
        ticket.hidden = this._determineTicketHidden(ticket);
        this._setTicketHiddenTimeout(ticket);
        // Create a copy of the original pricing to not alter the original pricing after applying coupons
        // (i) Meaning the pricing should always be used to show correct numbers of the ticket
        ticket.original_pricing = JSON.parse(JSON.stringify(ticket.pricing));
        return ticket;
    }
    /**
     *  Used to enrich the productGroup with his products models, based on the pivots.
     */
    _enrichGroup(group) {
        const groupProducts = [];
        // First get all the products of the group based on the pivots
        const productPivots = this._pivots.get({
            key: PIVOTS_KEY_GROUPS_PRODUCTS,
            item: {
                guid: group.guid,
                guid_key: 'group_id',
            },
        });
        forEach(productPivots, (pivot) => {
            const product = Model.getFrom(this.data, 'products_map', pivot.product_id);
            Model.addTo(groupProducts, {
                guid: product.guid,
                group_guid: group.guid,
                item: product,
                index: -1,
                uniqueness: pivot.uniqueness || null,
            });
        });
        // Compute the indexes of the products based on the pivots so they can easily be sorted
        const pivotSettings = {
            key: PIVOTS_KEY_GROUPS_PRODUCTS,
            item: {
                guid: '',
                guid_key: 'product_id',
            },
            belongs_to: {
                guid: group.guid,
                guid_key: 'group_id',
            },
        };
        this._pivots.computeIndexes(groupProducts, pivotSettings);
        // Make sure the order is correct based on the computed indexes
        groupProducts.sort(modelSort);
        group.products = groupProducts;
    }
    /**
     *  Used to enrich the product with his event dates
     *  and metadata models, based on the pivots.
     */
    _enrichProduct(product) {
        // Always clean the event dates and metadata, as the dataset to
        // load always contains all the pivots a product should contain
        product.event_dates = [];
        product.metadata = [];
        // The default pivot settings for getting the pivots of this product
        // (!) The key is overwritten per pivot type below
        const pivotSettings = {
            key: '',
            item: {
                guid: product.guid,
                guid_key: 'product_guid',
            },
        };
        // Enrich the product with his event dates
        const eventDatePivots = this._pivots.get(Object.assign(Object.assign({}, pivotSettings), { key: PIVOTS_KEY_PRODUCTS_EVENT_DATES }));
        forEach(eventDatePivots, (pivot) => {
            // Add reference to transformed event date
            Model.addTo(product.event_dates, Model.getFrom(this.data, 'event_dates_map', pivot.event_date_guid));
        });
        // Enrich the product with its metadata
        const metadataPivots = this._pivots.get(Object.assign(Object.assign({}, pivotSettings), { key: PIVOTS_KEY_PRODUCTS_METADATA }));
        forEach(metadataPivots, (pivot) => {
            const metadata = Model.getFrom(this.data, 'metadata_map', pivot.metadata_guid);
            const shopMetadata = {
                index: 0,
                guid: metadata.guid,
                item: metadata,
                productId: product.guid,
            };
            // Add reference to transformed metadata
            Model.addTo(product.metadata, shopMetadata);
        });
        // Compute their indexes based on the pivots
        this._pivots.computeIndexes(product.metadata, {
            key: PIVOTS_KEY_PRODUCTS_METADATA,
            item: {
                guid: '',
                guid_key: 'metadata_guid',
            },
        });
        // Sort the metadata based on their pivot-based computed index,
        product.metadata.sort(modelSort);
    }
    /**
     *  Used to enrich the forwarded ticket with his product
     *  group and product models, based on the pivots.
     */
    _enrichTicket(ticket) {
        // Always clean the groups, as the dataset to load always
        // contains all the pivots a ticket should contain
        ticket.groups = [];
        // Add his product groups based on the pivots
        let pivotSettings = {
            key: PIVOTS_KEY_TICKETS_GROUPS,
            item: {
                guid: ticket.guid,
                guid_key: 'ticket_guid',
            },
        };
        const groupPivots = this._pivots.get(pivotSettings);
        forEach(groupPivots, (pivot) => {
            // Add reference to transformed product group
            Model.addTo(ticket.groups, Model.getFrom(this.data, 'groups_map', pivot.group_guid));
        });
        // Compute their indexes based on the pivots
        pivotSettings = {
            key: PIVOTS_KEY_TICKETS_GROUPS,
            item: {
                guid: '',
                guid_key: 'group_guid',
            },
        };
        this._pivots.computeIndexes(ticket.groups, pivotSettings);
        // Sort the groups based on their pivot-based computed index,
        // which was added while setting the groups
        ticket.groups.sort(modelSort);
        // Get the products of the ticket, divided in all, required and optional
        ticket.products = this._getTicketProducts(ticket.guid, ticket.groups);
    }
    /**
     *  Used to enrich the forwarded event with a holder
     *  for his global products (which are set in the
     *  _setGlobalProducts method), his ticket and event
     *  date models, his start and end date based on his
     *  attached event dates and the sorted items list.
     */
    _enrichEvent(event) {
        // Create an Array for holding his global products, which
        // is filled in the _setGlobalProducts method
        event.global_products = [];
        // Add tickets based on the pivots
        event.tickets = [];
        const eventTicketPivots = this._pivots.get({
            key: PIVOTS_KEY_EVENTS_TICKETS,
            item: {
                guid: event.guid,
                guid_key: 'event_guid',
            },
        });
        forEach(eventTicketPivots, (pivot) => {
            // Add reference to transformed ticket
            Model.addTo(event.tickets, Model.getFrom(this.data, 'tickets_map', pivot.ticket_guid));
        });
        // Sort the tickets based on their pivot-based computed index
        event.tickets.sort(modelSort);
        // Compute the start and end date based on the nearest and
        // furthest event dates that are part of the event
        const eventDates = [];
        // Make sure to use the transformed event dates for more
        // easily computing the event's start and end date
        forOwn(this.data.event_dates_map, (eventDate) => {
            if (eventDate.event_id === event.guid) {
                eventDates.push(eventDate);
            }
        });
        // Compute the start and end based on his event dates
        event.start = null;
        event.end = null;
        if (eventDates.length) {
            let nearest;
            let furthest;
            if (eventDates.length === 1) {
                event.start = eventDates[0].start;
                event.end = eventDates[0].end;
            }
            else {
                // The model sort sorts on start by default
                nearest = eventDates.slice().sort(modelSort)[0];
                // The furthest should however be sorted based on his end date
                furthest = eventDates.slice().sort((eventDateA, eventDateB) => {
                    const timeA = eventDateA.end.getTime();
                    const timeB = eventDateB.end.getTime();
                    return timeA === timeB ? 0 : (timeA > timeB ? -1 : 1);
                })[0];
                event.start = nearest.start;
                event.end = furthest.end;
            }
        }
        // Add the sorted items
        this._addEventSortedItems(event);
        event.hidden = this._determineEventHidden(event, true);
    }
    /**
     *  A ticket can be hidden by coupons or by availability
     *
     *  If the ticket is not yet / no longer available,
     *  the ticket should be hidden if it is outside the availability margin.
     *
     *  If the ticket should be hidden without coupon,
     *  All coupons should be checked for the ticket to be visible.
     *
     *  @param ticket Ticket to determine hidden property for
     *  @param addedCouponItem When a coupon is added, the CouponApplicable item can be given to save looping over all coupon items.
     */
    _determineTicketHidden(ticket, addedCouponItem) {
        // Hide if now is before available from (taking margin into account).
        if (ticket.available_from_margin && Date.now() < ticket.available_from_margin.getTime()) {
            return true;
        }
        // Hide if now is after available until (taking margin into account).
        if (ticket.available_until_margin && Date.now() > ticket.available_until_margin.getTime()) {
            return true;
        }
        // Show if not hide without coupon.
        if (!ticket.hide_without_coupon) {
            return false;
        }
        // Determine Show/Hide directly if a coupon was added.
        if (addedCouponItem && addedCouponItem.ticket && addedCouponItem.ticket.guid === ticket.guid) {
            // Show if the coupon is for the current ticket
            return false;
        }
        // When this is run BEFORE the cart is initialized,
        // coupons can not be added yet anyway.
        // The ticket should be hidden.
        if (!this.cart) {
            return true;
        }
        // Determine Show/Hide for all coupons if no applicable coupon given.
        let hideTicket = true;
        forOwn(this.cart.getAppliedCoupons(), (appliedCoupon) => {
            forEach(appliedCoupon.appliesTo.shop, (applicableCoupon) => {
                if (applicableCoupon.ticket && applicableCoupon.ticket.guid === ticket.guid) {
                    hideTicket = false;
                }
            });
        });
        return hideTicket;
    }
    // noinspection JSMethodCanBeStatic
    /**
     *  An event item can be hidden when none of its tickets are visible
     */
    _determineEventItemHidden(item) {
        if (!item.tickets.length) {
            return true;
        }
        return !item.tickets.some((ticket) => !ticket.hidden);
    }
    /**
     *  Determine if an event has no visible items.
     *
     *  @param event The event to check the hidden value for
     *  @param checkAndUpdateItems Update the event items with their hidden values
     */
    _determineEventHidden(event, checkAndUpdateItems = false) {
        if (!event.items.length) {
            return true;
        }
        if (checkAndUpdateItems) {
            forEach(event.items, (item) => {
                item.hidden = this._determineEventItemHidden(item);
            });
        }
        return !event.items.some((item) => !item.hidden);
    }
    /**
     *  Register the timeouts for ticket availability.
     *
     *  E.g.: Show and hide depending on the availability window.
     */
    _setTicketHiddenTimeout(ticket) {
        // Let's take 1 ms to ensure the fluctuation in Date and comparisons will never
        // result in the wrong hidden value.
        const margin = 1;
        if (ticket.available_from_margin && (!ticket.available_until_margin || ticket.available_from_margin < ticket.available_until_margin)) {
            const msToVisible = ticket.available_from_margin.getTime() - Date.now();
            if (msToVisible > 0) {
                setLargeTimeout(() => {
                    ticket.hidden = this._determineTicketHidden(ticket);
                    const event = this.data.events_map[ticket.event_id];
                    event.hidden = this._determineEventHidden(event, true);
                }, Math.max(0, msToVisible) + margin);
            }
        }
        if (ticket.available_until_margin) {
            const msToHidden = ticket.available_until_margin.getTime() - Date.now();
            if (msToHidden > 0) {
                setLargeTimeout(() => {
                    ticket.hidden = this._determineTicketHidden(ticket);
                    const event = this.data.events_map[ticket.event_id];
                    event.hidden = this._determineEventHidden(event, true);
                }, Math.max(0, msToHidden) + margin);
            }
        }
        if (ticket.available_from && (!ticket.available_until || ticket.available_from < ticket.available_until)) {
            const msToAvailable = ticket.available_from.getTime() - Date.now();
            if (msToAvailable > 0) {
                setLargeTimeout(() => {
                    if (ticket.available_from && (ticket.available_from.getTime() - Date.now()) < 0 && ticket.status === 'not_sold_right_now') {
                        ticket.status = 'available';
                    }
                }, Math.max(0, msToAvailable) + margin);
            }
        }
        if (ticket.available_until) {
            const msToNotAvailable = ticket.available_until.getTime() - Date.now();
            if (msToNotAvailable > 0) {
                setLargeTimeout(() => {
                    if (ticket.available_until && (ticket.available_until.getTime() - Date.now()) < 0 && ticket.status === 'available') {
                        ticket.status = 'not_sold_right_now';
                    }
                }, Math.max(0, msToNotAvailable) + margin);
            }
        }
    }
    /**
     *  Used to compute and add the sorted items Array of
     *  an event. It includes a mixed up sorted collection
     *  of collapses and tickets that belong to the event.
     */
    _addEventSortedItems(event) {
        event.items = [];
        // Keep track of the guids that are already added to the items
        // to make sure collapses are not being added twice
        const addedItemGuids = {};
        // Method for finding the corresponding collapse of a ticket
        const correspondingCollapse = (ticketGuid) => {
            let ticketCollapse = undefined;
            forOwn(this.data.collapses_map, (collapse) => {
                if (collapse.ticket_guids.indexOf(ticketGuid) > -1) {
                    ticketCollapse = collapse;
                }
                return !ticketCollapse;
            });
            return ticketCollapse;
        };
        // Tickets are already sorted in the above method
        forEach(event.tickets, (ticket) => {
            const collapse = correspondingCollapse(ticket.guid);
            const item = collapse || ticket;
            const eventItem = {
                index: item.index,
                type: collapse ? 'collapse' : 'ticket',
                item,
                tickets: [],
                hidden: false,
            };
            if (!addedItemGuids[item.guid]) {
                // Collect refs to tickets in the collapse that belong to this event
                if (collapse) {
                    forEach(collapse.tickets, (ticket) => {
                        if (ticket.event_id === event.guid) {
                            eventItem.tickets.push(ticket);
                        }
                    });
                    // When no tickets are present for the current event in this collapse,
                    // continue to the next ticket.
                    // This can happen if the shop data does not follow the event-ticket relation
                    // via the ticket.event_id, but through event dates.
                    if (!eventItem.tickets.length) {
                        return;
                    }
                    // The default index of a collapse has been set to the index of his
                    // top ticket, but it might happen that the initial top ticket does
                    // not belong to this event. So we need to make sure it gets the
                    // index of the top ticket that belong to this event
                    eventItem.index = eventItem.tickets[0].index;
                }
                else {
                    // Or only add a ref to the single ticket, so it's always exposed in an uniform way
                    eventItem.tickets = [ticket];
                }
                eventItem.hidden = this._determineEventItemHidden(eventItem);
                event.items.push(eventItem);
                // Make sure this item is only added once
                addedItemGuids[item.guid] = item.guid;
            }
        });
        // Sort the items based on their computed index, which is working correctly
        // because every (event) collapse gets the index of his top ticket assigned
        event.items.sort(modelSort);
    }
    /**
     *  Used to get a sorted product collection of a ticket, which includes a
     *  collection of; all products (including optional and required),
     *  optional products and required products - all sorted.
     */
    _getTicketProducts(guid, groups) {
        const products = [];
        const ticketProducts = {
            all: [],
            optional: [],
            required: [],
        };
        // First get all the products of the ticket based on the pivots
        const productPivots = this._pivots.get({
            key: PIVOTS_KEY_PRODUCTS_TICKETS,
            item: {
                guid,
                guid_key: 'ticket_guid',
            },
        });
        forEach(productPivots, (pivot) => {
            // Add reference to transformed product
            Model.addTo(products, Model.getFrom(this.data, 'products_map', pivot.product_guid));
        });
        /**
         *  Used to define if the forwarded product is required, based on
         *  the fact if it's ungrouped or in a required group.
         */
        const isRequired = (productToCheck) => {
            // Every product that is not in a group is required
            let unGrouped = true;
            forEach(groups, (group) => {
                forEach(group.products, (groupProduct) => {
                    if (groupProduct.guid === productToCheck.guid) {
                        return unGrouped = false;
                    }
                });
                return unGrouped;
            });
            if (unGrouped) {
                return true;
            }
            // Every product that is in a required group is required
            let required = false;
            forEach(groups, (group) => {
                if (group.type === ProductGroupType.Required) {
                    forEach(group.products, (groupProduct) => {
                        if (groupProduct.guid === productToCheck.guid) {
                            required = true;
                        }
                        return !required;
                    });
                    return !required;
                }
            });
            return required;
        };
        /**
         *
         *  Create a ticket product for every product, holding its index, is_optional
         *  and pricing relative to (t)his ticket and a ref to the original product,
         *  as the index, is_optional and pricing are relative to the ticket.
         *
         *  (!) It should first create the collection before it can compute
         *  the indexes because the full collection is needed when the preceding
         *  sequence breaks and it sets the indexes based on provided order
         *
         */
        forEach(products, (product) => {
            const shopProduct = {
                index: -1,
                guid: product.guid,
                ticket_guid: guid,
                item: product,
                is_optional: !isRequired(product),
                pricing: JSON.parse(JSON.stringify(product.pricing)),
            };
            // The original product model does not hold coupons, because coupons
            // are being applied to the ShopProduct wrapper instead
            shopProduct.pricing.coupons = {};
            ticketProducts.all.push(shopProduct);
        });
        // Compute the indexes of the products based on the pivots so they can easily be sorted
        const pivotSettings = {
            key: PIVOTS_KEY_PRODUCTS_TICKETS,
            item: {
                guid: '',
                guid_key: 'product_guid',
            },
            belongs_to: {
                guid,
                guid_key: 'ticket_guid',
            },
        };
        this._pivots.computeIndexes(ticketProducts.all, pivotSettings);
        // Make sure the order is correct based on the computed indexes
        ticketProducts.all.sort(modelSort);
        // All products are required if no groups are present
        if (!groups.length) {
            ticketProducts.required = ticketProducts.all;
        }
        else {
            // Divide the product refs over optional and required
            forEach(ticketProducts.all, (ticketProduct) => {
                ticketProducts[ticketProduct.is_optional ? 'optional' : 'required'].push(ticketProduct);
            });
            // Sort the ticket products again in their own Array to make sure their
            // order is correct after dividing them in the two collections
            ticketProducts.optional.sort(modelSort);
            ticketProducts.required.sort(modelSort);
        }
        return ticketProducts;
    }
    // noinspection JSMethodCanBeStatic
    /**
     *  Used to transform a fees object; summing up the fixed and percentage
     *  fees (removing the distinction of the self and other) and removing
     *  the returned total.
     */
    _transformFees(fees) {
        // @ts-ignore
        fees.fixed = fees.self.fixed + fees.other.fixed;
        // @ts-ignore
        fees.percentage = fees.self.percentages + fees.other.percentages;
        // @ts-ignore
        delete fees.self;
        // @ts-ignore
        delete fees.other;
        // @ts-ignore
        delete fees.total;
        return fees;
    }
    _calculateCouponApplicableItems(coupon) {
        /**
         *  A collection that holds all the global products of the shop, including the
         *  ones who are part of an event and the ones who aren't.
         */
        const allGlobalProducts = {};
        // Collect all global products that are part of an event
        forEach(this.data.events, (event) => {
            forEach(event.global_products, (globalProduct) => {
                allGlobalProducts[globalProduct.guid] = globalProduct;
            });
        });
        // Collect all remaining global products that are not part of an event
        forEach(this.data.global_products, (globalProduct) => {
            allGlobalProducts[globalProduct.guid] = globalProduct;
        });
        /**
         *  A collection that holds all the required products in the shop, as the coupon
         *  should be applied to each of them as their pricing is loose from the original
         *  product and relative to the ticket he's in.
         *
         *  (i) Separate because this single level collection should be forwarded to the
         *  coupon's method for computing the applicable items
         */
        const allRequiredTicketProducts = {};
        /**
         *  A collection that holds a ref to the ticket of a specific ticket product,
         *  for easily getting it for adding it to the collection of applicable items.
         */
        const productTicket = {};
        // Fn to create a unique ticket/product id for saving refs in the above collections
        const constructTicketProductId = (ticketProduct) => {
            return `${ticketProduct.ticket_guid}-${ticketProduct.guid}`;
        };
        // Fill the above collections based on every ticket in the shop
        forEach(this.data.tickets, (ticket) => {
            forEach(ticket.products.required, (ticketProduct) => {
                // Because the coupon should be applied once on every ticket/product combination in the shop
                const ticketProductGuid = constructTicketProductId(ticketProduct);
                allRequiredTicketProducts[ticketProductGuid] = ticketProduct;
                productTicket[ticketProductGuid] = ticket;
            });
        });
        /**
         *  Used by the getApplicableItems() method to get the ticket of
         *  the product to which the coupon applies.
         *
         *  (i) It doesn't have a ticket when it's about a global product
         */
        const getTicketOfProduct = (ticketProduct) => {
            if (!ticketProduct.ticket_guid) {
                return undefined;
            }
            return productTicket[constructTicketProductId(ticketProduct)];
        };
        // Collect every ticket product and his ticket that is applicable for the coupon
        return Coupon.getApplicableItems(coupon.item, Object.assign(Object.assign({}, allGlobalProducts), allRequiredTicketProducts), getTicketOfProduct);
    }
}
//# sourceMappingURL=shop.js.map